/**
 * Alshallal Pastry Company HR System - Settings Layout Page
 * Phase 18.8.1: Real Settings page with navigation sidebar
 * Phase 18.10: Added Access & Roles (Super Admin only) and Module Visibility
 * 
 * Access: Super Admin, HR Manager, HR Admin (with varying subpage access)
 */
import React from 'react';
import { NavLink, Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  Settings, 
  Database, 
  AlertTriangle,
  Users,
  LayoutGrid,
  Mail,
  Archive,
  Bot,
  MapPin,
  FileText
} from 'lucide-react';
import { cn } from '@/lib/utils';

const SettingsPage = () => {
  const { hasRole } = useAuth();
  const { isRTL } = useLanguage();
  const location = useLocation();
  
  // RBAC, 2026-06: HR keeps HR configuration, IT INF owns system settings.
  // HR Assistant is not here - the operational desk has no settings screens.
  const canAccessSettings = hasRole('super_admin', 'hr_manager', 'hr_admin', 'it_inf');
  
  // Phase 18.10: Super Admin only pages
  const isSuperAdmin = hasRole('super_admin');
  
  if (!canAccessSettings) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">
          {isRTL ? 'غير مصرح' : 'Access Denied'}
        </h2>
        <p className="text-slate-600">
          {isRTL 
            ? 'ليس لديك صلاحية للوصول إلى الإعدادات'
            : 'You do not have permission to access settings'}
        </p>
      </div>
    );
  }
  
  // If at /settings root, redirect to custom-fields
  if (location.pathname === '/settings') {
    return <Navigate to="/settings/custom-fields" replace />;
  }
  
  const menuItems = [
    {
      path: '/settings/custom-fields',
      icon: Database,
      label: { en: 'Custom Fields', ar: 'الحقول المخصصة' },
      description: { en: 'Manage employee custom fields', ar: 'إدارة الحقول المخصصة للموظفين' },
      visible: hasRole('hr_manager', 'hr_admin', 'super_admin')  // HR data model, not system config
    },
    {
      path: '/settings/branches',
      icon: MapPin,
      label: { en: 'Branches', ar: 'الفروع' },
      description: { en: 'Manage company branches', ar: 'إدارة فروع الشركة' },
      visible: hasRole('hr_manager', 'hr_admin', 'super_admin')  // Phase 24.9: Moved from sidebar
    },
    {
      path: '/settings/access',
      icon: Users,
      label: { en: 'Access & Roles', ar: 'الوصول والأدوار' },
      description: { en: 'Manage user accounts', ar: 'إدارة حسابات المستخدمين' },
      // 2026-06: HR must be able to onboard a new hire without waiting for IT
      visible: hasRole('super_admin', 'hr_manager', 'hr_admin', 'it_inf')
    },
    {
      path: '/settings/module-visibility',
      icon: LayoutGrid,
      label: { en: 'Module Visibility', ar: 'إظهار الوحدات' },
      description: { en: 'Control module access per role', ar: 'التحكم في الوصول للوحدات حسب الدور' },
      visible: hasRole('super_admin', 'it_inf')  // 2026-06: system configuration
    },
    {
      path: '/settings/notifications',
      icon: Mail,
      label: { en: 'Push Notifications', ar: 'الإشعارات' },
      description: { en: 'Email broadcasts to employees', ar: 'بث البريد الإلكتروني للموظفين' },
      visible: hasRole('hr_manager', 'super_admin')  // Phase 20: HR Manager + Super Admin
    },
    {
      path: '/settings/repository',
      icon: Archive,
      label: { en: 'Repository', ar: 'المستودع' },
      description: { en: 'Archived users & employees', ar: 'المستخدمين والموظفين المؤرشفين' },
      visible: isSuperAdmin  // Phase 21: Super Admin only
    },
    {
      path: '/settings/ai-inbox',
      icon: Bot,
      label: { en: 'AI Inbox', ar: 'صندوق الذكاء الاصطناعي' },
      description: { en: 'Review AI assistant requests', ar: 'مراجعة طلبات المساعد الذكي' },
      visible: hasRole('hr_manager', 'hr_admin', 'super_admin')  // Phase 23: HR roles
    },
    {
      path: '/settings/letter-templates',
      icon: FileText,
      label: { en: 'Letter Templates', ar: 'قوالب الخطابات' },
      description: { en: 'Manage letter templates', ar: 'إدارة قوالب الخطابات' },
      visible: hasRole('hr_manager', 'super_admin')  // Phase T1: HR Manager + Super Admin
    },
    {
      path: '/settings/document-templates',
      icon: FileText,
      label: { en: 'Document Templates', ar: 'قوالب الوثائق' },
      description: { en: 'Manage employee document templates', ar: 'إدارة قوالب وثائق الموظفين' },
      visible: hasRole('hr_manager', 'super_admin')  // Phase D1: HR Manager + Super Admin
    },
  ].filter(item => item.visible);
  
  return (
    <div className="flex flex-col md:flex-row min-h-[calc(100vh-120px)]">
      {/* Settings Sidebar - horizontal on mobile, vertical on desktop */}
      <div className={cn(
        "bg-slate-50/50 p-3 md:p-4 flex-shrink-0",
        "border-b md:border-b-0 md:w-64",
        isRTL ? "md:border-s" : "md:border-e"
      )}>
        <div className="flex items-center gap-2 px-3 py-2 mb-2 md:mb-4">
          <Settings className="h-5 w-5 text-slate-600" />
          <h2 className="font-semibold text-slate-900">
            {isRTL ? 'الإعدادات' : 'Settings'}
          </h2>
        </div>
        
        {/* Mobile: horizontal scroll tabs, Desktop: vertical nav */}
        <nav className="flex md:flex-col gap-1 md:space-y-1 overflow-x-auto pb-2 md:pb-0 -mx-3 px-3 md:mx-0 md:px-0">
          {menuItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => cn(
                "flex items-center gap-2 md:gap-3 px-3 py-2 md:py-2.5 rounded-lg text-sm transition-colors whitespace-nowrap md:whitespace-normal",
                isActive 
                  ? "bg-blue-50 text-blue-700 font-medium" 
                  : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              )}
            >
              <item.icon className="h-4 w-4 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="truncate">
                  {isRTL ? item.label.ar : item.label.en}
                </div>
                <div className="hidden md:block text-xs text-slate-400 truncate">
                  {isRTL ? item.description.ar : item.description.en}
                </div>
              </div>
            </NavLink>
          ))}
        </nav>
      </div>
      
      {/* Main Content Area */}
      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
};

export default SettingsPage;
